import {isNotEmpty, isNotNil} from 'ramda';

import {useLazyGetRegistrationPlateDecoderQuery} from '@dms/api/vehicle';

import {ApiError} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {is404ApiError} from '../../../utils/is404ApiError';

export type RegistrationPlateDecodeOutcome =
  | {type: 'vin'; vin: string}
  | {type: 'notFound'}
  | {type: 'error'};

/**
 * Resolves a registration plate to a VIN and collapses the endpoint's response and error
 * matrix into a single outcome. A plate the provider does not know about is not an error:
 * it arrives as a 404, an empty body, or a `null` VIN, and all three mean the same thing.
 */
export function useDecodeRegistrationPlate() {
  const [getRegistrationPlateDecoder, {isFetching}] = useLazyGetRegistrationPlateDecoderQuery();

  const decodeRegistrationPlate = (
    registrationPlate: string
  ): Promise<RegistrationPlateDecodeOutcome> =>
    getRegistrationPlateDecoder({registrationPlate})
      .unwrap()
      .then((response): RegistrationPlateDecodeOutcome => {
        const vin = response?.vin;

        return isNotNil(vin) && isNotEmpty(vin) ? {type: 'vin', vin} : {type: 'notFound'};
      })
      .catch((error: ApiError): RegistrationPlateDecodeOutcome => {
        if (is404ApiError(error)) {
          return {type: 'notFound'};
        }

        handleApiError(error);

        return {type: 'error'};
      });

  return {decodeRegistrationPlate, isDecodingRegistrationPlate: isFetching};
}
